import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import styles from '../styles/NavButton.module.css';
import { getBrutalistStyles } from '../styles/brutalist';
import BrutalistNavigation from '../components/BrutalistNavigation';
import { API_ENDPOINTS, getApiUrl } from '../config/api';

const IniciarSesion = () => {
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const brutalistStyles = getBrutalistStyles(isDarkMode);
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false
  });
  
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Show success message from registration if available
  useEffect(() => {
    if (location.state?.message) {
      setSuccessMessage(location.state.message);
      // Clear the message from location state
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    // Clear errors when user starts typing
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const response = await fetch(getApiUrl(API_ENDPOINTS.schoolLogin), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: formData.email,
          password: formData.password,
          remember: formData.rememberMe
        })
      });

      const data = await response.json();

      if (!response.ok) {
        // Handle specific error cases
        switch (response.status) {
          case 401:
            throw new Error('Email o contraseña incorrectos');
          case 423:
            throw new Error('Cuenta bloqueada temporalmente. Por favor, inténtalo más tarde');
          default:
            throw new Error(data.message || 'Error al iniciar sesión');
        }
      }

      // Check if we have the expected data structure
      if (!data.success || !data.school || !data.token) {
        throw new Error('Respuesta del servidor inválida');
      }

      // Update auth context with login data
      await login({
        token: data.token,
        school: data.school,
        rememberMe: formData.rememberMe
      });
      
      // Redirect to dashboard
      navigate('/dashboard');
    } catch (err) {
      setError(err.message);
      // Clear password field on error
      setFormData(prev => ({
        ...prev,
        password: ''
      }));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <BrutalistNavigation />
      <div className="container" style={{
        ...brutalistStyles.formContainer,
        marginTop: '4rem',
        '@media (max-width: 768px)': {
          marginTop: '6rem'
        }
      }}>
        <h1 style={brutalistStyles.brutalistTitle}>
          INICIAR SESIÓN
        </h1>
        
        {successMessage && (
          <div style={{
            ...brutalistStyles.brutalistSuccess,
            marginBottom: '1.5rem'
          }}>
            {successMessage}
          </div>
        )}

        {error && (
          <div style={{
            ...brutalistStyles.brutalistError,
            marginBottom: '1.5rem'
          }}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '1.5rem'
        }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            <label
              htmlFor="email"
              style={brutalistStyles.brutalistLabel}
            >
              Email
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              style={brutalistStyles.brutalistInput}
              required
              autoComplete="email"
              disabled={isLoading}
            />
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            <label
              htmlFor="password"
              style={brutalistStyles.brutalistLabel}
            >
              Contraseña
            </label>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              style={brutalistStyles.brutalistInput}
              required
              autoComplete="current-password"
              disabled={isLoading}
            />
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <input
              type="checkbox"
              id="rememberMe"
              name="rememberMe"
              checked={formData.rememberMe}
              onChange={handleChange}
              style={brutalistStyles.brutalistCheckbox}
              disabled={isLoading}
            />
            <label
              htmlFor="rememberMe"
              style={{
                fontWeight: 600,
                fontSize: '0.9rem',
                cursor: 'pointer'
              }}
            >
              Recordar sesión
            </label>
          </div>

          <button
            type="submit"
            style={{
              ...brutalistStyles.brutalistButton,
              ...(isLoading && brutalistStyles.disabledState)
            }}
            disabled={isLoading}
          >
            {isLoading ? 'Iniciando sesión...' : 'Iniciar Sesión'}
          </button>

          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '1rem',
            alignItems: 'center',
            marginTop: '1.5rem'
          }}>
            <Link
              to="/recuperar-contrasena"
              style={brutalistStyles.brutalistLink}
            >
              ¿Olvidaste tu contraseña?
            </Link>
            <Link
              to="/registrar-nueva-autoescuela"
              className={`${styles.navButton} ${styles.testCompleto} ${isDarkMode ? styles.dark : ''}`}
              style={{
                width: '100%',
                textAlign: 'center',
                marginTop: '0.5rem'
              }}
            >
              <span className={styles.title}>Registrar Nueva Autoescuela</span>
            </Link>
          </div>
        </form>
      </div>
    </>
  );
};

export default IniciarSesion;